import { Fragment, useEffect, useRef, useState } from 'react';
import { Alert, Button, GlobalStyles, Portal, Stack } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { PerformanceRequest, WorkloadReport } from './types';
import { buildPerformancePrintDocument, canPrintPerformance, PerformancePrintDocument } from './print-model';
import { performancePrintStyles } from './print-styles';

interface Props {
  report: WorkloadReport;
  request: PerformanceRequest;
  agencyId: number;
  agencyName?: string;
  workflowName: string;
  responseClock?: 'elapsed' | 'business';
  disabled?: boolean;
}

export function PerformancePrintContent({
  document,
  emptyLabel,
}: {
  document: PerformancePrintDocument;
  emptyLabel: string;
}) {
  return (
    <article data-performance-print-root aria-label={document.title}>
      <header>
        <h1>{document.title}</h1>
        <dl>
          {document.metadata.map(([label, value], index) => (
            <Fragment key={index}>
              <dt>{label}</dt>
              <dd>{value}</dd>
            </Fragment>
          ))}
        </dl>
      </header>
      {document.warnings.map((warning, index) => (
        <p className="performance-print-warning" key={index}>
          {warning}
        </p>
      ))}
      {document.sections.map((section, index) => (
        <section key={index}>
          <h2>{section.title}</h2>
          {section.notes.map((note, noteIndex) => (
            <p key={noteIndex} className="performance-print-note">
              {note}
            </p>
          ))}
          {section.tables.map((table, tableIndex) => (
            <Fragment key={tableIndex}>
              {table.title && <h3>{table.title}</h3>}
              {table.note && <p className="performance-print-note">{table.note}</p>}
              <table>
                <thead>
                  <tr>
                    {table.headers.map((header, headerIndex) => (
                      <th key={headerIndex} scope="col">
                        {header}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {table.rows.length ? (
                    table.rows.map((row, rowIndex) => (
                      <tr key={rowIndex}>
                        {row.map((cell, cellIndex) => (
                          <td key={cellIndex}>{cell}</td>
                        ))}
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={table.headers.length}>{emptyLabel}</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </Fragment>
          ))}
        </section>
      ))}
      <footer>
        {document.metadata
          .filter(([label]) => label)
          .map(([label, value]) => `${label}: ${value}`)
          .join(' · ')}
        <p>{document.warnings[0]}</p>
      </footer>
    </article>
  );
}

export function PerformancePrintReport({ disabled = false, ...input }: Props) {
  const { t, i18n } = useTranslation();
  const [snapshot, setSnapshot] = useState<PerformancePrintDocument | null>(null);
  const [failed, setFailed] = useState(false);
  const button = useRef<HTMLButtonElement>(null);
  const available = !disabled && canPrintPerformance(input.report, input.request);
  const text = (key: string, values?: Record<string, unknown>) => String(t(`workflowPerformance.${key}`, values));

  useEffect(() => {
    setSnapshot(null);
  }, [
    input.report,
    input.request,
    input.agencyId,
    input.agencyName,
    input.workflowName,
    input.responseClock,
    disabled,
  ]);

  useEffect(() => {
    if (!snapshot) return;
    let cancelled = false;
    let firstFrame = 0;
    let secondFrame = 0;
    const done = () => {
      setSnapshot(null);
      button.current?.focus();
    };
    window.addEventListener('afterprint', done);
    // The snapshot and its print styles must be committed before the browser captures them.
    void Promise.resolve(document.fonts?.ready).then(() => {
      if (cancelled) return;
      firstFrame = window.requestAnimationFrame(() => {
        secondFrame = window.requestAnimationFrame(() => {
          if (cancelled) return;
          try {
            window.print();
          } catch {
            setFailed(true);
            done();
          }
        });
      });
    });
    return () => {
      cancelled = true;
      window.cancelAnimationFrame(firstFrame);
      window.cancelAnimationFrame(secondFrame);
      window.removeEventListener('afterprint', done);
    };
  }, [snapshot]);

  const print = () => {
    if (!available || snapshot) return;
    setFailed(false);
    setSnapshot(
      buildPerformancePrintDocument(
        {
          ...input,
          zone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          locale: i18n.language,
          originLabel: (origin) => String(t(`cases.originatedByChannel.${origin}`, { defaultValue: origin })),
          statusLabel: (status) => String(t(`cases.status.${status}`, { defaultValue: status })),
        },
        text,
      ),
    );
  };

  return (
    <Stack spacing={1}>
      <Button
        ref={button}
        onClick={print}
        disabled={!available || Boolean(snapshot)}
        aria-busy={Boolean(snapshot)}
        variant="outlined"
      >
        {text('print.action')}
      </Button>
      {failed && <Alert severity="error">{text('print.failed')}</Alert>}
      {snapshot && (
        <>
          <GlobalStyles styles={performancePrintStyles} />
          <Portal>
            <PerformancePrintContent document={snapshot} emptyLabel={text('print.empty')} />
          </Portal>
        </>
      )}
    </Stack>
  );
}
